import React from "react";
import { ArrowUpRight, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useContactForm } from "../hooks/useContactForm";
import { useTranslation } from "react-i18next";

const fieldStyle = "w-full rounded-none border-0 border-b border-black/20 bg-transparent px-0 py-2 text-sm shadow-none focus-visible:ring-0 focus-visible:border-[#0057FF]";
const labelStyle = "text-[10px] uppercase tracking-[0.16em] text-[#0e0e10]/55 block mb-2";

const ContactField = ({ label, name, ...props }) => (
  <div className="min-w-0">
    <label htmlFor={`contact-${name}`} data-testid={`contact-${name}-label`} className={labelStyle}>{label}</label>
    <Input id={`contact-${name}`} name={name} data-testid={`contact-${name}-input`} className={fieldStyle} {...props} />
  </div>
);

export const ContactForm = () => {
  const { t } = useTranslation();
  const { form, sending, notice, handleChange, handleSubmit } = useContactForm();
  return (
    <form onSubmit={handleSubmit} noValidate data-testid="contact-form" aria-busy={sending}
      className="reveal-on-scroll min-w-0 bg-white/60 border border-black/10 rounded-sm p-7 md:p-10"
      style={{ "--reveal-delay": "120ms" }}>
      <fieldset disabled={sending} className="min-w-0">
        <div className="grid sm:grid-cols-2 gap-5 mb-5">
          <ContactField label={t("contact.fields.name")} name="name" value={form.name} onChange={handleChange}
            autoComplete="name" placeholder={t("contact.placeholders.name")} required minLength={2} maxLength={120} />
          <ContactField label={t("contact.fields.email")} name="email" type="email" value={form.email} onChange={handleChange}
            autoComplete="email" placeholder={t("contact.placeholders.email")} required maxLength={254} />
        </div>
        <div className="mb-5">
          <ContactField label={t("contact.fields.topic")} name="topic" value={form.topic} onChange={handleChange}
            placeholder={t("contact.placeholders.topic")} maxLength={160} />
        </div>
        <div className="mb-7">
          <label htmlFor="contact-message" data-testid="contact-message-label" className={labelStyle}>{t("contact.fields.message")}</label>
          <Textarea id="contact-message" name="message" data-testid="contact-message-input"
            value={form.message} onChange={handleChange} rows={5} required minLength={5} maxLength={5000}
            placeholder={t("contact.placeholders.message")} className={`${fieldStyle} resize-none`} />
        </div>
        <div className="hidden" aria-hidden="true">
          <label htmlFor="contact-website">{t("contact.fields.website")}</label>
          <input id="contact-website" data-testid="contact-website-honeypot" name="website"
            value={form.website} onChange={handleChange} tabIndex={-1} autoComplete="off" />
        </div>
        <Button type="submit" disabled={sending} data-testid="contact-submit-button"
          className="group w-full h-14 rounded-full bg-[#0e0e10] text-white text-sm font-medium uppercase tracking-[0.12em] gap-3 hover:bg-[#0057FF] transition-colors duration-300">
          {sending ? <><Loader2 className="animate-spin" aria-hidden="true" /> {t("contact.sending")}</> :
            <>{t("contact.submit")} <ArrowUpRight className="hover-arrow" aria-hidden="true" /></>}
        </Button>
      </fieldset>
      {notice && (
        <div data-testid={`contact-${notice.type}-message`} role={notice.type === "error" ? "alert" : "status"}
          className={`mt-5 flex items-start gap-2 text-sm leading-relaxed break-words ${notice.type === "error" ? "text-red-700" : "text-[#0057FF]"}`}>
          {notice.type === "error" ? <AlertCircle size={18} className="mt-0.5 shrink-0" aria-hidden="true" /> :
            <CheckCircle2 size={18} className="mt-0.5 shrink-0" aria-hidden="true" />}
          <p className="min-w-0">{t(notice.key)}</p>
        </div>
      )}
    </form>
  );
};
